import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { AuthService } from '../../../core/services/auth/auth.service';
import { authType } from '../../../constants/auth-type';
import { emailValidatorRegex } from '../../../constants/validation-regex.constants';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService } from '../../../core/services/modal/modal.service';
import { ACCOUNT_API_ERRORS } from '../../../constants/account-api-errors';
import { isPlatformBrowser } from '@angular/common';
import { ProfileHelper } from '../../../core/classes/profile-helper/profile-helper';
import { TranslateService } from '@ngx-translate/core';
import { CookieAppService } from '@shared/common/services/cookie-app/cookie-app.service';
import { CustomerService } from '../../../core/services/customer/customer.service';
import { MessageService } from '../../../core/services/messages/messages.service';
import { PASSWORD_STRENGTH } from '../../../constants/password.constant';
import { cookieVariables } from '../../../constants/cookie-variables.constants';
import { LocationService } from '../../../core/services/location/location.service';
import { externalLinks } from '../../../constants/external-links.constants';
import { modalVars } from '../../../constants/modal-vars';
import { TrackingService } from '../../../core/services/tracking/tracking.service';
import { takeUntil } from 'rxjs/operators';
import { subscriptionSources } from '../../../constants/subscription-sources.constants';
import { ReCaptcha2Component } from 'ngx-captcha';
import { environment } from '../../../../environments/environment';
import { EnvironmentType } from '@shared/common/enums/env-type.enum';
import { Subject } from 'rxjs';
import { ToastService } from '@limehome/spark-ui-angular';

@Component({
  selector: 'app-auth-modal',
  templateUrl: './auth-modal.component.html',
  styleUrls: ['./auth-modal.component.scss'],
  standalone: false
})
export class AuthModalComponent extends ProfileHelper implements OnInit, OnDestroy {
  authTypeEnum = authType;
  authType = authType.logIn;
  authForm: UntypedFormGroup;
  forgotPasswordForm: UntypedFormGroup;
  resetPasswordForm: UntypedFormGroup;
  logInForm: UntypedFormGroup;
  signUpForm: UntypedFormGroup;
  isAuthFormSubmitted = false;
  isRegistrationSent = false;
  isForgotPasswordSent = false;
  isResetPasswordSent = false;
  createdAccountCongratsModalId = 'created-account';
  createdAccountNewsletterOnlyCongratsModalId = 'created-newsletter-only-account';
  errorModalId = 'error-modal';
  authModalId = 'auth';
  forgotPasswordModalId = 'forgot-password';
  resetPasswordModalId = 'reset-password';
  isForgotPasswordRequestSuccess = false;
  isResetPasswordRequestSuccess = false;
  accountApiErrors = ACCOUNT_API_ERRORS;
  accountError: string;
  urlResetToken: string;
  isFetching = false;
  passwordStrength = PASSWORD_STRENGTH;
  externalLinks = externalLinks;
  modalVars = modalVars;
  isModalOpen = false;
  isOnlyLogin = false;
  isRedirecting = false;
  activeRoutePath: string;
  token: string;
  loginParam: string;
  shouldDisplayAccountCongratsMessage = false;
  isForgotPasswordModalOpened = false;
  private readonly _destruction: Subject<void> = new Subject<void>();
  isAccountAlreadyActive = false;

  readonly checkoutPagePattern = /^\/listings\/checkout/i;

  // Google reCAPTCHA
  siteKey = environment.reCaptchaKey;
  recaptchaLang: string;
  recaptchaSize: 'compact' | 'normal' = 'normal';
  @ViewChild('captchaEl') captchaEl: ReCaptcha2Component;
  isCheckoutPage: boolean;

  @Input() set isAuthModalOpen(v: boolean) {
    this.isModalOpen = v;
  }
  @Input() set authModalActiveTab(tabName: authType) {
    if (tabName === authType.signUp) {
      this.authType = authType.signUp;
      this.toggleForm(this.authTypeEnum.signUp);
    }
  }
  @Output() authModalClosed = new EventEmitter<string>();

  constructor(
    private readonly authService: AuthService,
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    public modalService: ModalService,
    public translateService: TranslateService,
    public cookieAppService: CookieAppService,
    public customerService: CustomerService,
    public messageService: MessageService,
    public locationService: LocationService,
    public trackingService: TrackingService,
    @Inject(PLATFORM_ID) public platformId: Object,
    public store: Store,
    private readonly toastService: ToastService
  ) {
    super(translateService, cookieAppService, locationService, platformId);
  }

  ngOnInit() {
    this.logInForm = this.initLogInForm();
    this.signUpForm = this.initSignUpForm();
    this.initAuthForm();
    this.activatedRoute.url.pipe(takeUntil(this._destruction)).subscribe((urlSegments) => {
      this.activeRoutePath = urlSegments && urlSegments.length ? urlSegments[0].path : '';
      this.isRedirecting = false;
      const queryParamMap = this.activatedRoute.snapshot.queryParamMap;
      this.token = queryParamMap.get('token');
      this.loginParam = queryParamMap.get('login');
      const isNewsletterOnly = queryParamMap.get('isNewsletterOnly') == 'true';

      if (isPlatformBrowser(this.platformId)) {
        if (this.activeRoutePath === 'activate' && this.token) {
          this.activateCustomer(this.token, isNewsletterOnly);
        }
        if (this.activeRoutePath === 'reset-password' && this.token) {
          this.urlResetToken = this.token;
          this.checkResetPasswordToken(this.urlResetToken);
        }
        if (this.activeRoutePath === 'complete-profile') {
          this.completeProfile();
        }
      }
      if (this.activeRoutePath === 'login' || this.activeRoutePath === 'signup' || this.loginParam) {
        if (this.activeRoutePath === 'signup') {
          this.toggleForm(this.authTypeEnum.signUp);
        }
        setTimeout(() => {
          this.isModalOpen = true;
          this.openModal(this.authModalId);
        });
      }
    });

    this.authService.isOnlyLogin.pipe(takeUntil(this._destruction)).subscribe((isOnlyLogin) => {
      this.isOnlyLogin = isOnlyLogin;
    });

    this.authService.passedErrorMessage.pipe(takeUntil(this._destruction)).subscribe((passedErrorMessage) => {
      if (passedErrorMessage !== null) {
        this.accountError = passedErrorMessage;
      }
    });
  }

  get agreeControl(): FormControl {
    return this.authForm.get('agree') as FormControl;
  }

  get newsletterControl(): FormControl {
    return this.authForm.get('newsletter') as FormControl;
  }

  get shouldShowNewMembershipTitle() {
    return this.authType === this.authTypeEnum.signUp;
  }

  ngOnDestroy() {
    this._destruction.next();
    this._destruction.complete();
  }

  activateCustomer(token: string, isNewsletterOnly: boolean) {
    this.customerService.activateCustomer(token).subscribe({
      next: () => {
        // Show a different success message if the account is newsletterOnly
        if (isNewsletterOnly) {
          this.openModal(this.createdAccountNewsletterOnlyCongratsModalId);
          return;
        }
        this.shouldDisplayAccountCongratsMessage = true;
        setTimeout(() => {
          this.openModal(this.authModalId);
        });
      },
      error: (error) => {
        if (isNewsletterOnly) {
          this.accountError = 'generic.internal-server-error';
        } else {
          this.accountError = this.getErrorTranslationKey(error);
        }
        this.openModal(this.errorModalId);
      }
    });
  }

  clearAuthFormSettings() {
    this.isRegistrationSent = false;
    this.logInForm.reset();
    this.signUpForm.reset();
    this.accountError = null;
    this.toggleForm(this.authTypeEnum.logIn);
    this.authService.isOnlyLogin.next(false);
    this.authModalClosed.next('');
    this.checkActivateRoute();
  }

  clearForgotPasswordForm() {
    this.forgotPasswordForm.reset();
    this.accountError = null;
    this.isForgotPasswordSent = false;
    this.isForgotPasswordRequestSuccess = false;
    this.isForgotPasswordModalOpened = false;
    this.checkActivateRoute();
  }

  checkActivateRoute() {
    if (this.activeRoutePath === 'activate' && this.token && !this.isForgotPasswordModalOpened && !this.isRedirecting) {
      this.goToHome();
    }
  }

  closeAuthModal() {
    this.closeModal(this.authModalId);
    this.clearAuthFormSettings();
  }

  closeSuccessfulModal(id: string, isNewsletterOnly: boolean, isNotRedirectedHome?: boolean) {
    this.closeModal(id);
    if (!isNotRedirectedHome) {
      this.goToHome();
    }
    if (!isNewsletterOnly) {
      setTimeout(() => {
        this.openModal(this.authModalId);
      });
    }
  }

  closeModal(id: string) {
    this.modalService.close(id);
  }

  closeActivationErrorModal(id: string) {
    this.closeModal(id);
    this.goToHome();
  }

  closeForgotPasswordModal(id: string) {
    this.clearForgotPasswordForm();
    this.closeModal(id);
  }

  closeResetPasswordModal(id: string) {
    this.goToHome('?login=true');

    if (this.isResetPasswordRequestSuccess) {
      this.closeSuccessfulModal(id, false, true);
    }
  }

  checkResetPasswordToken(token: string) {
    this.customerService.checkResetPasswordToken(token).subscribe({
      next: () => {
        setTimeout(() => {
          this.openModal(this.resetPasswordModalId);
          this.initResetPasswordForm();
        });
      },
      error: (error: HttpErrorResponse) => {
        this.accountError = this.getErrorTranslationKey(error);

        if (this.accountError === 'account.password-reset-token-invalid') {
          this.accountError = 'account.checked-password-reset-token-invalid';
        }
        this.openModal(this.errorModalId);
      }
    });
  }

  completeProfile() {
    this.redirectToProfile().then((redirected) => {
      if (redirected) {
        return;
      }

      this.isModalOpen = true;
      this.isOnlyLogin = true;
      this.openModal(this.authModalId);
    });
  }

  checkUrlPath() {
    if (this.activeRoutePath === 'complete-profile') {
      this.redirectToProfile();
    }

    this.isRedirecting = true;
  }

  redirectToProfile() {
    return this.router.navigate(['/account/profile']);
  }

  showForgotPasswordModal(event) {
    event.preventDefault();
    this.isForgotPasswordModalOpened = true;
    this.closeAuthModal();
    this.openModal(this.forgotPasswordModalId);
    this.initForgotPasswordForm();
  }

  goToHome(urlParams?: string) {
    // redirect to angular homepage
    // this.router.navigate(['/']);

    // redirect to the root - wp homepage
    let url = urlParams ? '/' + urlParams : '/';
    this.locationService.redirectToExternal(url);
  }

  openModal(id: string) {
    this.modalService.open(id);
  }

  initSignUpForm() {
    return this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        email: ['', Validators.compose([Validators.required, Validators.pattern(emailValidatorRegex)])],
        agree: [false, [Validators.required, Validators.requiredTrue]],
        newsletter: [false],
        honeyCheckbox: [false]
      },
      {
        validator: [this.validatePasswordStrength('password')]
      }
    );
  }

  validatePasswordStrength(controlName: string) {
    return (formGroup: UntypedFormGroup) => {
      if (!this.isModalOpen) {
        return null;
      }
      this.validateStrength(controlName, formGroup);
    };
  }

  initLogInForm() {
    return this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
      email: ['', Validators.compose([Validators.required, Validators.pattern(emailValidatorRegex)])],
      honeyCheckbox: [false]
    });
  }

  initAuthForm() {
    if (this.authType === this.authTypeEnum.logIn) {
      this.authForm = this.logInForm;
    } else {
      this.authForm = this.signUpForm;
    }
  }

  initForgotPasswordForm() {
    this.forgotPasswordForm = this.fb.group({
      email: ['', Validators.compose([Validators.required, Validators.pattern(emailValidatorRegex)])]
    });
  }

  initResetPasswordForm() {
    this.resetPasswordForm = this.fb.group(
      {
        newPassword: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required, Validators.minLength(8)]]
      },
      {
        validator: [
          this.customerService.matchPasswords('newPassword', 'confirmPassword'),
          (formGroup: UntypedFormGroup) => {
            return this.validateStrength('newPassword', formGroup);
          }
        ]
      }
    );
  }

  toggleForm(type: authType) {
    this.authType = type;
    this.isAuthFormSubmitted = false;
    this.accountError = null;
    this.isFetching = false;
    this.initAuthForm();
    this.resetSignUpPasswordScore();
  }

  async sendAuth() {
    // This property is used to hide a checkbox from bots
    // More details about the solution in https://stackoverflow.com/questions/36227376/better-honeypot-implementation-form-anti-spam
    if (this.authForm.controls.honeyCheckbox.value) {
      return;
    }

    this.isAuthFormSubmitted = true;
    this.accountError = null;

    if (this.authForm.invalid) {
      return;
    }

    this.isFetching = true;

    const { email, password, newsletter } = this.authForm.value;

    if (this.authType === this.authTypeEnum.signUp) {
      this.signUp(email, password, !!newsletter, subscriptionSources.HEADER);
    } else {
      this.login(email, password);
    }
  }

  /**
   * It makes a HTTP request to sign-up the user
   *
   * @param email - The email address of the user
   * @param password - The password the user entered
   * @param newsletter - This informs us whether the user checked to subscribe to the newsletter.
   * @param source - This is the source of the subscription.
   * For example, if the user is signing up from the home page, the `subscriptionSource` would be "homepage".
   */
  async signUp(email: string, password: string, newsletter: boolean, source: string) {
    this.customerService
      .signUp({
        email,
        password,
        newsletter,
        source
      })
      .subscribe({
        next: (response) => {
          this.handleSuccessSignUp(!!response?.active);
        },
        error: (error: HttpErrorResponse) => {
          this.handleLogInOrSingUpError(error);
        }
      });
  }

  /**
   * It makes a HTTP request to log-in the user
   *
   * @param email - The email address of the user
   * @param password - The password the user entered
   */
  login(email: string, password: string) {
    this.authService.logIn(email, password).subscribe({
      next: (response) => {
        this.handleSuccessLogIn(response);
      },
      error: (error) => {
        this.handleLogInOrSingUpError(error);
      }
    });
  }

  handleSuccessSignUp(isActive: boolean) {
    this.isFetching = false;
    if (isPlatformBrowser(this.platformId)) {
      this.isCheckoutPage = this.checkoutPagePattern.test(window.location.pathname); // added for button in modal after success signup
    }
    this.isAccountAlreadyActive = isActive;
    this.isRegistrationSent = true;
  }

  handleSuccessLogIn(response) {
    this.checkUrlPath();
    this.checkLanguage(response);

    const isDevEnvironment = [EnvironmentType.LOCAL, EnvironmentType.DEVELOP].includes(environment.environment);
    if (!isDevEnvironment) {
      this.setIsInternalCookie(response.internal);
    }

    this.checkActivateRoute();
    this.closeAuthModal();
    const message = this.translateService.instant('auth.login-success');
    this.toastService.show({ message, type: 'success' });
  }

  handleLogInOrSingUpError(error: HttpErrorResponse) {
    this.isFetching = false;
    this.accountError = this.getErrorTranslationKey(error);
    this.shouldDisplayAccountCongratsMessage = false;
  }

  sendForgotPassword() {
    this.isForgotPasswordSent = true;
    this.accountError = null;

    if (this.forgotPasswordForm.invalid) {
      return;
    }

    const { email, recaptcha } = this.forgotPasswordForm.value;
    this.isFetching = true;

    this.customerService.resetPasswordRequest(email, recaptcha).subscribe({
      next: () => {
        this.isForgotPasswordRequestSuccess = true;
        this.isFetching = false;
      },
      error: (error) => {
        this.accountError = this.getErrorTranslationKey(error);
        this.resetCaptcha();
        this.isFetching = false;
      }
    });
  }

  sendResetPassword() {
    this.isResetPasswordSent = true;
    this.accountError = null;

    if (this.resetPasswordForm.valid) {
      this.isFetching = true;

      this.customerService
        .resetPassword(this.resetPasswordForm.controls.newPassword.value, this.urlResetToken)
        .subscribe({
          next: () => {
            this.isResetPasswordRequestSuccess = true;
            this.isFetching = false;
          },
          error: (error) => {
            this.isFetching = false;
            this.accountError = this.getErrorTranslationKey(error);
          }
        });
    }
  }

  checkLanguage(response) {
    // If the user has a preferred language, then set the website language
    if (response.profile && response.profile.languageCode) {
      const languageCode = response.profile.languageCode;

      this.messageService.sendLanguageChange(languageCode);
    }
  }

  setIsInternalCookie(isInternal: boolean): void {
    if (isInternal) {
      this.cookieAppService.setCookie(cookieVariables.isUserInternal, '1', 1); // This cookie will be removed on logout
    }
  }

  /**
   * Handles event when reCAPTCHA is reset or expires
   */
  recaptchaOnReset() {
    this.forgotPasswordForm.patchValue({ recaptcha: null });
  }

  /**
   * It resets the reCAPTCHA
   */
  resetCaptcha() {
    this.captchaEl?.resetCaptcha();
  }

  get recaptchaCtrl(): UntypedFormControl {
    return this.forgotPasswordForm.get('recaptcha') as UntypedFormControl;
  }
}
